import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { fetchCurrentUser, UserProfile } from '../../api/auth';
import {
  OrderStatus,
  fetchOrder,
  OrderResponse,
  addItemToCart,
} from '../../api/orders';
import { fetchProductVariant, type ProductVariant } from '../../api/productVariants';
import { fetchOrderAiInput, runOrderAiAnalysis } from '../../api/orderAi';
import { getAiHealthScoreStyles } from '../../utils/aiUtils';
import Notification, {
  type NotificationElement,
} from '../../components/midone/components/Base/Notification';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';
import CustomerVehicleSelection, {
  CustomerVehicleSelectionSnapshot,
} from './components/CustomerVehicleSelection';
import OrderStepPlaceholder from './components/OrderStepPlaceholder';
import InspectionChecklistPanel from './components/InspectionChecklistPanel';
import OrderProductServicesCart from './components/OrderProductServicesCart';
import OrderCustomerApproval from './components/OrderCustomerApproval';
import OrderManagerApproval from './components/OrderManagerApproval';
import OrderAssignTechnician from './components/OrderAssignTechnician';
import OrderTechnicianCompleted from './components/OrderTechnicianCompleted';
import OrderGatePass from './components/OrderGatePass';
import PreDeliveryChecklistPanel from './components/PreDeliveryChecklistPanel';
import OrderPaymentPos from './components/OrderPaymentPos';
import { Banner, fetchActiveBanners } from '../../api/banners';
import {
  fetchBrandRelatedVariants,
  fetchModelRelatedVariants,
  type RelatedVariant,
} from '../../api/vehicleRelatedVariants';
import {
  fetchProductRelatedVariants,
  type ProductRelatedVariant,
} from '../../api/productRelatedVariants';
import { fetchInventories } from '../../api/inventories';

interface StepNavLink {
  key: string;
  label: string;
  description?: string;
}

const NAV_LINKS: StepNavLink[] = [
  {
    key: 'customer-vehicle',
    label: 'Customer & Vehicle Selection',
    description: 'Capture the customer profile and link their vehicle to open the job card.',
  },
  {
    key: 'vehicle-inspection',
    label: 'Vehicle Inspection Checklist',
    description: 'Document inspection findings using predefined checklists.',
  },
  {
    key: 'services-products',
    label: 'Add Services & Products',
    description: 'Build the estimate with labour lines and parts.',
  },
  {
    key: 'manager-approval',
    label: 'Manager Approval',
    description: 'Escalate to a manager for review prior to execution.',
  },
  {
    key: 'customer-approval',
    label: 'Customer Approval',
    description: 'Send the estimate to the customer and log their approval status.',
  },
  {
    key: 'technician-work',
    label: 'Assign Technician',
    description: 'Assign technicians and capture work in progress updates.',
  },
  {
    key: 'technician-completed',
    label: 'Job Completed',
    description: 'Confirm work completion and prep for QA checks.',
  },
  {
    key: 'pre-delivery-checklist',
    label: 'Pre-delivery Checklist',
    description: 'Record pre-delivery checks before handover.',
  },
  {
    key: 'payment-pos',
    label: 'Payment at POS',
    description: 'Collect payment and link the POS transaction.',
  },
  {
    key: 'gate-pass',
    label: 'Gate Pass & Delivery',
    description: 'Generate the gate pass after payment is completed and hand over the vehicle to the customer.',
  },
];

const DEFAULT_STEP_KEY = NAV_LINKS[0].key;

const STATUS_PROGRESSION: OrderStatus[] = [
  'initiated',
  'draft',
  'approved',
  'confirmed',
  'in_progress',
  'completed',
  'verified',
  'done',
  'delivered',
];

const STATUS_COMPLETION_STEPS: Array<{ status: OrderStatus; step: string }> = [
  { status: 'approved', step: 'manager-approval' },
  { status: 'confirmed', step: 'customer-approval' },
  { status: 'in_progress', step: 'technician-work' },
  { status: 'completed', step: 'technician-completed' },
  { status: 'verified', step: 'pre-delivery-checklist' },
  { status: 'done', step: 'payment-pos' },
  { status: 'delivered', step: 'gate-pass' },
];

const getStatusRank = (status: OrderStatus | null | undefined) => {
  if (!status) {
    return -1;
  }

  const index = STATUS_PROGRESSION.indexOf(status);
  return index;
};

const isChecklistResponseFilled = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return true;
};

const OrderNewPage: FC = () => {
  const navigate = useNavigate();
  const { step } = useParams<{ step?: string }>();
  const [searchParams] = useSearchParams();
  const orderIdParam = searchParams.get('orderId');
  const isEditing = Boolean(orderIdParam);
  const [existingOrder, setExistingOrder] = useState<OrderResponse | null>(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiResult, setAiResult] = useState<unknown | null>(null);
  const [aiAddToCartLoadingId, setAiAddToCartLoadingId] = useState<string | null>(null);
  const [aiAddedVariantIds, setAiAddedVariantIds] = useState<string[]>([]);
  const [aiVariantsById, setAiVariantsById] = useState<Record<string, ProductVariant | null>>({});
  const [aiRelatedById, setAiRelatedById] = useState<Record<string, ProductRelatedVariant[] | null>>({});
  const [aiRelatedLoadingId, setAiRelatedLoadingId] = useState<string | null>(null);
  const [aiRelatedErrorById, setAiRelatedErrorById] = useState<Record<string, string | null>>({});
  const [aiRelatedStockByVariantId, setAiRelatedStockByVariantId] = useState<Record<string, number | null>>({});
  const [aiToast, setAiToast] = useState<{ id: number; title: string; message: string } | null>(null);
  const aiToastRef = useRef<NotificationElement | null>(null);
  const [relatedModalOpen, setRelatedModalOpen] = useState(false);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState<string | null>(null);
  const [brandRelated, setBrandRelated] = useState<RelatedVariant[]>([]);
  const [modelRelated, setModelRelated] = useState<RelatedVariant[]>([]);
  const [relatedAddedVariantIds, setRelatedAddedVariantIds] = useState<string[]>([]);
  const [relatedAddToCartLoadingId, setRelatedAddToCartLoadingId] = useState<string | null>(null);
  const [banners, setBanners] = useState<Banner[]>([]);

  useEffect(() => {
    fetchActiveBanners()
      .then((data) => setBanners(data))
      .catch(() => setBanners([]));
  }, []);

  const activeKey = useMemo(() => {
    const normalized = step?.toLowerCase();
    if (!normalized) {
      return DEFAULT_STEP_KEY;
    }
    const match = NAV_LINKS.some((link) => link.key === normalized);
    return match ? normalized : DEFAULT_STEP_KEY;
  }, [step]);

  useEffect(() => {
    if (!step || step.toLowerCase() !== activeKey) {
      const query = orderIdParam ? `?orderId=${orderIdParam}` : '';
      navigate(`/order-management/new/${activeKey}${query}`, { replace: true });
    }
  }, [activeKey, navigate, step, orderIdParam]);

  const activeLink = useMemo(
    () => NAV_LINKS.find((link) => link.key === activeKey) ?? NAV_LINKS[0],
    [activeKey],
  );

  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [userError, setUserError] = useState<string | null>(null);

  useEffect(() => {
    fetchCurrentUser()
      .then((profile) => {
        setCurrentUser(profile.user);
        setLoadingUser(false);
      })
      .catch((error) => {
        setUserError(error instanceof Error ? error.message : 'Unable to load the current user profile.');
        setLoadingUser(false);
      });
  }, []);

  useEffect(() => {
    if (!orderIdParam) {
      setExistingOrder(null);
      setOrderLoading(false);
      setOrderError(null);
      return;
    }

    setOrderLoading(true);
    setOrderError(null);

    fetchOrder(orderIdParam)
      .then((order) => {
        setExistingOrder(order);
      })
      .catch((error) => {
        setExistingOrder(null);
        setOrderError(error instanceof Error ? error.message : 'Failed to load order details.');
      })
      .finally(() => setOrderLoading(false));
  }, [orderIdParam]);

  // Reset AI / related markers when switching orders
  useEffect(() => {
    setAiAddedVariantIds([]);
    setAiRelatedById({});
    setAiRelatedErrorById({});
    setRelatedAddedVariantIds([]);
  }, [orderIdParam]);

  const handleSelectNav = (key: string) => {
    if (key === activeKey) {
      return;
    }

    if (!orderIdParam && key !== DEFAULT_STEP_KEY) {
      return;
    }

    const query = orderIdParam ? `?orderId=${orderIdParam}` : '';
    navigate(`/order-management/new/${key}${query}`);
  };

  const aiAnalysis = useMemo(() => {
    const metadata = existingOrder?.metadata ?? null;
    const fromMetadata =
      metadata && typeof metadata === 'object'
        ? (metadata as any).aiVehicleHealth ?? null
        : null;
    const fromAnalysis = (existingOrder as any)?.aiAnalysis ?? null;
    return aiResult ?? fromAnalysis ?? fromMetadata ?? null;
  }, [existingOrder, aiResult]);

  const handleOpenAiModal = async () => {
    if (!orderIdParam || aiRunning) {
      setAiModalOpen(true);
      return;
    }
    setAiModalOpen(true);
    setAiError(null);
    setAiResult(null);
    setAiRunning(true);
    try {
      const input = await fetchOrderAiInput(orderIdParam);
      if (!input.ok || !input.hasInspection) {
        setAiError('Inspection checklist data is required for AI recommendations.');
        return;
      }
      const result = await runOrderAiAnalysis(orderIdParam);
      if (!result.ok || !result.payload) {
        setAiError('AI recommendations could not be generated. Please try again later.');
        return;
      }
      setAiResult(result.payload);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to run AI recommendations.';
      setAiError(message);
    } finally {
      setAiRunning(false);
    }
  };

  const handleOpenRelatedModal = async () => {
    setRelatedModalOpen(true);
    setRelatedError(null);
    setBrandRelated([]);
    setModelRelated([]);

    const vehicle = existingOrder?.customerVehicle;
    const brandId =
      (vehicle?.vehicleBrandId as string | undefined) ??
      (vehicle?.vehicleBrand?.id as string | undefined) ??
      null;
    const modelId =
      (vehicle?.vehicleModelId as string | undefined) ??
      (vehicle?.vehicleModel?.id as string | undefined) ??
      null;

    if (!brandId && !modelId) {
      setRelatedError('Select a vehicle to view related products.');
      return;
    }

    setRelatedLoading(true);
    try {
      const [brandResult, modelResult] = await Promise.all([
        brandId ? fetchBrandRelatedVariants(String(brandId)) : Promise.resolve<RelatedVariant[]>([]),
        modelId ? fetchModelRelatedVariants(String(modelId)) : Promise.resolve<RelatedVariant[]>([]),
      ]);
      setBrandRelated(brandResult);
      setModelRelated(modelResult);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to load related products.';
      setRelatedError(message);
    } finally {
      setRelatedLoading(false);
    }
  };

  // Reset AI-added cart markers and related caches when switching orders
  useEffect(() => {
    setAiAddedVariantIds([]);
    setAiRelatedById({});
    setAiRelatedErrorById({});
    setAiRelatedStockByVariantId({});
  }, [orderIdParam]);

  useEffect(() => {
    if (!aiAnalysis) {
      setAiVariantsById({});
      setAiAddedVariantIds([]);
      return;
    }

    const recs = (aiAnalysis as any).recommendations ?? {};
    const now = (recs.recommendedNow?.products ?? []) as Array<{ id: string }>;
    const future = (recs.recommendedFuture?.products ?? []) as Array<{ id: string }>;
    const ids = Array.from(
      new Set(
        [...now, ...future]
          .map((item) => item.id)
          .filter((id): id is string => Boolean(id && id.length > 0)),
      ),
    );

    if (!ids.length) {
      return;
    }

    let cancelled = false;
    (async () => {
      const updates: Record<string, ProductVariant | null> = {};
      for (const id of ids) {
        try {
          const variant = await fetchProductVariant(id);
          updates[id] = variant;
        } catch {
          updates[id] = null;
        }
      }
      if (!cancelled && Object.keys(updates).length) {
        setAiVariantsById((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [aiAnalysis]);

  useEffect(() => {
    if (!aiToast) {
      return;
    }

    aiToastRef.current?.showToast?.();

    const handle = window.setTimeout(() => {
      aiToastRef.current?.hideToast?.();
      setAiToast(null);
    }, 2500);

    return () => {
      window.clearTimeout(handle);
      aiToastRef.current?.hideToast?.();
    };
  }, [aiToast?.id]);

  const renderRelatedSummary = () => {
    const formatAmount = (value: string | number | null | undefined) => {
      const numeric = typeof value === 'number' ? value : Number(value ?? 0);

      if (Number.isNaN(numeric)) {
        return '0.00';
      }

      return numeric.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    const canAddToCart =
      !!existingOrder &&
      !['delivered', 'verified', 'completed'].includes(existingOrder.status);

    const existingCartVariantIds = new Set(
      [
        ...((existingOrder?.items ?? [])
          .map((item: any) => item.productVariantId)
          .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0)),
        ...aiAddedVariantIds,
        ...relatedAddedVariantIds,
      ],
    );

    const brandName =
      (existingOrder?.customerVehicle as any)?.vehicleBrand?.name ??
      (existingOrder?.customerVehicle as any)?.vehicleBrandName ??
      null;
    const modelName =
      (existingOrder?.customerVehicle as any)?.vehicleModel?.name ??
      (existingOrder?.customerVehicle as any)?.vehicleModelName ??
      null;

    if (relatedLoading) {
      return (
        <p className="text-sm text-slate-600">
          Loading related products…
        </p>
      );
    }

    if (relatedError) {
      return (
        <p className="text-sm text-danger">
          {relatedError}
        </p>
      );
    }

    const hasBrand = brandRelated.length > 0;
    const hasModel = modelRelated.length > 0;

    if (!hasBrand && !hasModel) {
      return (
        <p className="text-sm text-slate-600">
          No related items found for this vehicle.
        </p>
      );
    }

    return (
      <div className="space-y-4">
        {hasBrand ? (
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Brand related products{brandName ? ` – ${brandName}` : ''}
            </h3>
            <ul className="mt-2 space-y-2 text-sm text-slate-700">
              {brandRelated.map((relation) => {
                const variant = relation.productVariant;
                const variantId = variant.id;
                const isAlreadyInCart = existingCartVariantIds.has(variantId);
                return (
                  <li key={relation.id} className="rounded-md bg-slate-50 p-2">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="font-semibold text-slate-900">
                          {variant.name}
                        </div>
                        {variant.erpId ? (
                          <div className="text-[11px] uppercase tracking-wide text-slate-500">
                            SKU: {variant.erpId}
                          </div>
                        ) : null}
                        <div className="mt-1 text-xs text-slate-800">
                          Price:{' '}
                          <span className="font-semibold">
                            {formatAmount(variant.retailPrice)}
                          </span>
                        </div>
                      </div>
                      {canAddToCart ? (
                        <button
                          type="button"
                          className="mt-2 inline-flex items-center justify-center rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary/90 disabled:opacity-60 sm:mt-0"
                          disabled={isAlreadyInCart || relatedAddToCartLoadingId === variantId}
                          onClick={async () => {
                            if (!orderIdParam || isAlreadyInCart) return;
                            setRelatedAddToCartLoadingId(variantId);
                            try {
                              const rawPrice = (variant as any).retailPrice;
                              const unitPrice =
                                rawPrice !== null && rawPrice !== undefined
                                  ? Number(rawPrice)
                                  : 0;

                              await addItemToCart(orderIdParam, {
                                productVariantId: variantId,
                                description: variant.name,
                                itemType: 'service',
                                quantity: 1,
                                unitPrice,
                                metadata: null,
                              });
                              setRelatedAddedVariantIds((prev) =>
                                prev.includes(variantId) ? prev : [...prev, variantId],
                              );
                              window.dispatchEvent(
                                new CustomEvent('order-cart-refresh', {
                                  detail: {
                                    orderId: orderIdParam,
                                    toast: {
                                      id: Date.now(),
                                      title: 'Success',
                                      message: 'Added to cart.',
                                    },
                                  },
                                }),
                              );
                            } catch (error) {
                              const message =
                                error instanceof Error
                                  ? error.message
                                  : 'Failed to add related product to cart.';
                              setRelatedError(message);
                              window.dispatchEvent(
                                new CustomEvent('order-cart-refresh', {
                                  detail: {
                                    orderId: orderIdParam,
                                    toast: {
                                      id: Date.now(),
                                      title: 'Error',
                                      message,
                                    },
                                  },
                                }),
                              );
                            }
                            setRelatedAddToCartLoadingId(null);
                          }}
                        >
                          {isAlreadyInCart
                            ? 'Already added to cart'
                            : relatedAddToCartLoadingId === variantId
                              ? 'Adding…'
                              : 'Add to cart'}
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {hasModel ? (
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Model related products{modelName ? ` – ${modelName}` : ''}
            </h3>
            <ul className="mt-2 space-y-2 text-sm text-slate-700">
              {modelRelated.map((relation) => {
                const variant = relation.productVariant;
                const variantId = variant.id;
                const isAlreadyInCart = existingCartVariantIds.has(variantId);
                return (
                  <li key={relation.id} className="rounded-md bg-slate-50 p-2">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="font-semibold text-slate-900">
                          {variant.name}
                        </div>
                        {variant.erpId ? (
                          <div className="text-[11px] uppercase tracking-wide text-slate-500">
                            SKU: {variant.erpId}
                          </div>
                        ) : null}
                        <div className="mt-1 text-xs text-slate-800">
                          Price:{' '}
                          <span className="font-semibold">
                            {formatAmount(variant.retailPrice)}
                          </span>
                        </div>
                      </div>
                      {canAddToCart ? (
                        <button
                          type="button"
                          className="mt-2 inline-flex items-center justify-center rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary/90 disabled:opacity-60 sm:mt-0"
                          disabled={isAlreadyInCart || relatedAddToCartLoadingId === variantId}
                          onClick={async () => {
                            if (!orderIdParam || isAlreadyInCart) return;
                            setRelatedAddToCartLoadingId(variantId);
                            try {
                              const rawPrice = (variant as any).retailPrice;
                              const unitPrice =
                                rawPrice !== null && rawPrice !== undefined
                                  ? Number(rawPrice)
                                  : 0;

                              await addItemToCart(orderIdParam, {
                                productVariantId: variantId,
                                description: variant.name,
                                itemType: 'service',
                                quantity: 1,
                                unitPrice,
                                metadata: null,
                              });
                              setRelatedAddedVariantIds((prev) =>
                                prev.includes(variantId) ? prev : [...prev, variantId],
                              );
                              window.dispatchEvent(
                                new CustomEvent('order-cart-refresh', {
                                  detail: {
                                    orderId: orderIdParam,
                                    toast: {
                                      id: Date.now(),
                                      title: 'Success',
                                      message: 'Added to cart.',
                                    },
                                  },
                                }),
                              );
                            } catch (error) {
                              const message =
                                error instanceof Error
                                  ? error.message
                                  : 'Failed to add related product to cart.';
                              setRelatedError(message);
                              window.dispatchEvent(
                                new CustomEvent('order-cart-refresh', {
                                  detail: {
                                    orderId: orderIdParam,
                                    toast: {
                                      id: Date.now(),
                                      title: 'Error',
                                      message,
                                    },
                                  },
                                }),
                              );
                            }
                            setRelatedAddToCartLoadingId(null);
                          }}
                        >
                          {isAlreadyInCart
                            ? 'Already added to cart'
                            : relatedAddToCartLoadingId === variantId
                              ? 'Adding…'
                              : 'Add to cart'}
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    );
  };

  const renderAiSummary = () => {
    if (aiError) {
      return (
        <p className="text-sm text-danger">
          {aiError}
        </p>
      );
    }
    if (aiRunning && !aiAnalysis) {
      return (
        <p className="text-sm text-slate-600">
          Running AI analysis for this order…
        </p>
      );
    }
    if (!aiAnalysis) {
      return (
        <p className="text-sm text-slate-600">
          AI recommendations will be generated after the inspection checklist data is submitted.
        </p>
      );
    }
    const scores = (aiAnalysis as any).scores ?? {};
    const overall = scores.overall ?? {};
    const sections = scores.sections ?? {};
    const recs = (aiAnalysis as any).recommendations ?? {};
    const now = (recs.recommendedNow?.products ?? []) as Array<{
      id: string;
      name: string;
      reason: string;
    }>;
    const future = (recs.recommendedFuture?.products ?? []) as Array<{
      id: string;
      name: string;
      reason: string;
    }>;
    const formatAmount = (value: string | number | null | undefined) => {
      const numeric = typeof value === 'number' ? value : Number(value ?? 0);

      if (Number.isNaN(numeric)) {
        return '0.00';
      }

      return numeric.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };
    const canAddToCart =
      !!existingOrder &&
      !['delivered', 'verified', 'completed'].includes(existingOrder.status);

    const existingCartVariantIds = new Set(
      [
        ...((existingOrder?.items ?? [])
          .map((item: any) => item.productVariantId)
          .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0)),
        ...aiAddedVariantIds,
      ],
    );

    const sectionKeys: Array<keyof typeof sections> = [
      'tyres',
      'engine',
      'brakes',
      'suspension',
      'electrical',
      'body',
    ];

    const overallStyles = getAiHealthScoreStyles(overall.score);

    return (
      <div className="space-y-4">
        <div className={`rounded-lg border p-4 ${overallStyles.container}`}>
          <h3 className="text-sm font-semibold text-slate-900">Overall health</h3>
          <>
            <p className={`mt-1 text-sm ${overallStyles.text}`}>
              <span className="font-semibold">{overall.score ?? '—'}</span>{' '}
              <span className={`uppercase text-xs tracking-wide ${overallStyles.label}`}>
                {overall.label ?? ''}
              </span>
            </p>
            {overall.summary ? (
              <p className={`mt-2 text-sm ${overallStyles.text}`}>{overall.summary}</p>
            ) : null}
          </>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Section scores
          </h3>
          <div className="mt-2 grid gap-3 md:grid-cols-2">
            {sectionKeys.map((key) => {
              const keyName = String(key);
              const sec = (sections as any)[keyName];
              if (!sec) return null;
              const secStyles = getAiHealthScoreStyles(sec.score);
              return (
                <div
                  key={keyName}
                  className={`rounded-lg border p-3 ${secStyles.container}`}
                >
                  <div className="flex items-center justify-between text-sm">
                    <span className={`font-semibold ${secStyles.text}`}>
                      {keyName.replace(/^\w/, (c) => c.toUpperCase())}
                    </span>
                    <span className={`text-xs font-medium ${secStyles.label}`}>
                      {sec.score ?? '—'} • {sec.label ?? ''}
                    </span>
                  </div>
                  {sec.summary ? (
                    <p className={`mt-1 text-xs ${secStyles.text}`}>
                      {sec.summary}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Recommended now
          </h3>
          {now.length === 0 ? (
            <p className="mt-1 text-sm text-slate-600">No immediate actions.</p>
          ) : (
            <ul className="mt-2 space-y-2 text-sm text-slate-700">
              {now.map((item) => {
                const isAlreadyInCart = existingCartVariantIds.has(item.id);
                const variant = aiVariantsById[item.id];
                const isService = variant?.isCreatedByWorkshopManagement ?? false;
                const related = aiRelatedById[item.id] ?? null;
                const relatedError = aiRelatedErrorById[item.id] ?? null;
                const isExpanded = !!related;
                return (
                  <li key={item.id} className="rounded-md bg-emerald-50 p-2">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="font-semibold text-emerald-900">
                          {item.name}
                        </div>
                        <div className="text-xs text-emerald-800">
                          {item.reason}
                        </div>
                        {variant ? (
                          <div className="mt-0.5 text-[11px] text-emerald-800">
                            Type:{' '}
                            <span className="font-semibold">
                              {isService ? 'Service' : 'Product'}
                            </span>
                          </div>
                        ) : null}
                        {(() => {
                          if (!variant) {
                            return null;
                          }
                          return (
                            <div className="mt-1 text-xs text-emerald-900">
                              Price:{' '}
                              <span className="font-semibold">
                                {formatAmount(variant.retailPrice)}
                              </span>
                            </div>
                          );
                        })()}
                        {isService ? (
                          <div className="mt-2">
                            <button
                              type="button"
                              className="text-[11px] font-semibold text-blue-600 underline underline-offset-2 hover:text-blue-700 disabled:opacity-60"
                              onClick={async () => {
                                if (!variant) return;
                                if (!aiRelatedById[item.id]) {
                                  setAiRelatedLoadingId(item.id);
                                  setAiRelatedErrorById((prev) => ({
                                    ...prev,
                                    [item.id]: null,
                                  }));
                                  try {
                                    const list = await fetchProductRelatedVariants(variant.id);
                                    setAiRelatedById((prev) => ({
                                      ...prev,
                                      [item.id]: list,
                                    }));
                                    const stockUpdates: Record<string, number | null> = {};
                                    for (const relation of list) {
                                      const rv = relation.relatedVariant;
                                      if (!rv.erpId) {
                                        stockUpdates[rv.id] = null;
                                        continue;
                                      }
                                      try {
                                        const inv = await fetchInventories({
                                          page: 1,
                                          limit: 100,
                                          variantErpId: rv.erpId,
                                        });
                                        const total =
                                          (inv.data ?? []).reduce((sum: number, row: any) => {
                                            const qty = Number(row.stock ?? 0);
                                            return sum + (Number.isNaN(qty) ? 0 : qty);
                                          }, 0) ?? 0;
                                        stockUpdates[rv.id] = total;
                                      } catch {
                                        stockUpdates[rv.id] = null;
                                      }
                                    }
                                    if (Object.keys(stockUpdates).length) {
                                      setAiRelatedStockByVariantId((prev) => ({
                                        ...prev,
                                        ...stockUpdates,
                                      }));
                                    }
                                  } catch (err) {
                                    const message =
                                      err instanceof Error
                                        ? err.message
                                        : 'Failed to load related items.';
                                    setAiRelatedErrorById((prev) => ({
                                      ...prev,
                                      [item.id]: message,
                                    }));
                                  } finally {
                                    setAiRelatedLoadingId(null);
                                  }
                                } else {
                                  // toggle off
                                  setAiRelatedById((prev) => {
                                    const next = { ...prev };
                                    delete next[item.id];
                                    return next;
                                  });
                                }
                              }}
                              disabled={aiRelatedLoadingId === item.id}
                            >
                              {aiRelatedLoadingId === item.id
                                ? 'Loading related items…'
                                : isExpanded
                                  ? 'Hide related products'
                                  : 'Show related products'}
                            </button>
                            {isExpanded ? (
                              <div className="mt-1 rounded-md border border-emerald-100 bg-white/80 p-2">
                                {relatedError ? (
                                  <p className="text-[11px] text-danger">{relatedError}</p>
                                ) : !related || related.length === 0 ? (
                                  <p className="text-[11px] text-slate-500">
                                    No related products configured for this service.
                                  </p>
                                ) : (
                                  <ul className="space-y-1">
                                    {related.map((relation) => {
                                      const rv = relation.relatedVariant;
                                      const rvInCart = existingCartVariantIds.has(rv.id);
                                      const stock = aiRelatedStockByVariantId[rv.id];
                                      const outOfStock = stock !== undefined && stock !== null && stock <= 0;
                                      return (
                                        <li
                                          key={relation.id}
                                          className="flex flex-col gap-1 rounded border border-emerald-100 bg-emerald-50/60 p-2 sm:flex-row sm:items-center sm:justify-between"
                                        >
                                          <div>
                                            <p className="text-xs font-semibold text-emerald-900">
                                              {rv.name}
                                            </p>
                                            <p className="text-[11px] text-emerald-800">
                                              Type:{' '}
                                              <span className="font-semibold">
                                                {rv.isCreatedByWorkshopManagement ? 'Service' : 'Product'}
                                              </span>
                                            </p>
                                            {stock !== undefined && stock !== null ? (
                                              <p className="text-[11px] text-emerald-800">
                                                Stock:{' '}
                                                <span className="font-semibold">
                                                  {stock > 0 ? stock : 'Out of stock'}
                                                </span>
                                              </p>
                                            ) : null}
                                          </div>
                                          {canAddToCart ? (
                                            <button
                                              type="button"
                                              className="mt-1 inline-flex items-center justify-center rounded-lg bg-primary px-3 py-1 text-[11px] font-semibold text-white shadow-sm hover:bg-primary/90 disabled:opacity-60 sm:mt-0"
                                              disabled={
                                                rvInCart || outOfStock || aiAddToCartLoadingId === rv.id
                                              }
                                              onClick={async () => {
                                                if (!orderIdParam || rvInCart) return;
                                                setAiAddToCartLoadingId(rv.id);
                                                try {
                                                  const rawPrice = (rv as any).retailPrice;
                                                  const unitPrice =
                                                    rawPrice !== null && rawPrice !== undefined
                                                      ? Number(rawPrice)
                                                      : 0;

                                                  await addItemToCart(orderIdParam, {
                                                    productVariantId: rv.id,
                                                    description: rv.name,
                                                    itemType: rv.isCreatedByWorkshopManagement
                                                      ? 'service'
                                                      : 'product',
                                                    quantity: 1,
                                                    unitPrice,
                                                    metadata: {
                                                      aiRecommendedRelatedOf: item.id,
                                                      aiReason: item.reason,
                                                    },
                                                  });
                                                  setAiAddedVariantIds((prev) =>
                                                    prev.includes(rv.id) ? prev : [...prev, rv.id],
                                                  );
                                                  window.dispatchEvent(
                                                    new CustomEvent('order-cart-refresh', {
                                                      detail: {
                                                        orderId: orderIdParam,
                                                        toast: {
                                                          id: Date.now(),
                                                          title: 'Success',
                                                          message: 'Added to cart.',
                                                        },
                                                      },
                                                    }),
                                                  );
                                                } catch (error) {
                                                  const message =
                                                    error instanceof Error
                                                      ? error.message
                                                      : 'Failed to add related item to cart.';
                                                  setAiError(message);
                                                  window.dispatchEvent(
                                                    new CustomEvent('order-cart-refresh', {
                                                      detail: {
                                                        orderId: orderIdParam,
                                                        toast: {
                                                          id: Date.now(),
                                                          title: 'Error',
                                                          message,
                                                        },
                                                      },
                                                    }),
                                                  );
                                                } finally {
                                                  setAiAddToCartLoadingId(null);
                                                }
                                              }}
                                            >
                                              {rvInCart
                                                ? 'Already added'
                                                : aiAddToCartLoadingId === rv.id
                                                  ? 'Adding…'
                                                  : 'Add to cart'}
                                            </button>
                                          ) : null}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      {canAddToCart ? (
                        <button
                          type="button"
                          className="mt-2 inline-flex items-center justify-center rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary/90 disabled:opacity-60 sm:mt-0"
                          disabled={isAlreadyInCart || aiAddToCartLoadingId === item.id}
                          onClick={async () => {
                            if (!orderIdParam || isAlreadyInCart) return;
                            setAiAddToCartLoadingId(item.id);
                            try {
                              const variant = await fetchProductVariant(item.id);
                              const rawPrice = (variant as any).retailPrice;
                              const unitPrice =
                                rawPrice !== null && rawPrice !== undefined
                                  ? Number(rawPrice)
                                  : 0;

                              await addItemToCart(orderIdParam, {
                                productVariantId: variant.id,
                                description: item.name,
                                itemType: 'service',
                                quantity: 1,
                                unitPrice,
                                metadata: {
                                  aiRecommendedNow: true,
                                  aiReason: item.reason,
                                },
                              });
                              setAiAddedVariantIds((prev) =>
                                prev.includes(item.id) ? prev : [...prev, item.id],
                              );
                              if (activeKey === 'services-products') {
                                // Trigger a background refresh for the services/products cart
                                // and show a cart-level toast, without closing this modal.
                                window.dispatchEvent(
                                  new CustomEvent('order-cart-refresh', {
                                    detail: {
                                      orderId: orderIdParam,
                                      toast: {
                                        id: Date.now(),
                                        title: 'Success',
                                        message: 'Added to cart.',
                                      },
                                    },
                                  }),
                                );
                              } else {
                                setAiToast({
                                  id: Date.now(),
                                  title: 'Success',
                                  message: 'Added to cart.',
                                });
                              }
                            } catch (error) {
                              const message =
                                error instanceof Error
                                  ? error.message
                                  : 'Failed to add recommended service to cart.';
                              setAiError(message);
                              if (activeKey === 'services-products') {
                                window.dispatchEvent(
                                  new CustomEvent('order-cart-refresh', {
                                    detail: {
                                      orderId: orderIdParam,
                                      toast: {
                                        id: Date.now(),
                                        title: 'Error',
                                        message,
                                      },
                                    },
                                  }),
                                );
                              } else {
                                setAiToast({
                                  id: Date.now(),
                                  title: 'Error',
                                  message,
                                });
                              }
                            } finally {
                              setAiAddToCartLoadingId(null);
                            }
                          }}
                        >
                          {isAlreadyInCart
                            ? 'Already added to cart'
                            : aiAddToCartLoadingId === item.id
                              ? 'Adding…'
                              : 'Add to cart'}
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Recommended in future
          </h3>
          {future.length === 0 ? (
            <p className="mt-1 text-sm text-slate-600">
              No future recommendations.
            </p>
          ) : (
            <ul className="mt-2 space-y-2 text-sm text-slate-700">
              {future.map((item) => (
                <li key={item.id} className="rounded-md bg-slate-50 p-2">
                  <div className="font-semibold text-slate-900">
                    {item.name}
                  </div>
                  <div className="text-xs text-slate-800">
                    {item.reason}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const completedSteps = useMemo(() => {
    const steps = new Set<string>();

    if (!existingOrder) {
      return steps;
    }

    steps.add('customer-vehicle');

    const inspectionEntries = existingOrder.checklists?.filter((entry) => entry.type === 'inspection') ?? [];
    if (
      inspectionEntries.length > 0 &&
      inspectionEntries.every((entry) => isChecklistResponseFilled(entry.response))
    ) {
      steps.add('vehicle-inspection');
    }

    if ((existingOrder.items ?? []).length > 0) {
      steps.add('services-products');
    }

    if ((existingOrder.technicians ?? []).length > 0) {
      steps.add('technician-work');
    }

    if (existingOrder.customerApproved) {
      steps.add('customer-approval');
    }

    if (existingOrder.managerApproved) {
      steps.add('manager-approval');
    }

    if (existingOrder.technicianCompletedTheJobAt) {
      steps.add('technician-completed');
    }

    const statusRank = getStatusRank(existingOrder.status);
    if (statusRank >= 0) {
      STATUS_COMPLETION_STEPS.forEach(({ status, step }) => {
        if (statusRank >= getStatusRank(status)) {
          steps.add(step);
        }
      });
    }

    return steps;
  }, [existingOrder]);

  const defaultCustomerVehicleSnapshot = useMemo(() => {
    if (!existingOrder) {
      return undefined;
    }

    const snapshot: CustomerVehicleSelectionSnapshot = {
      customer: existingOrder.customer ?? null,
      customerVehicleId: existingOrder.customerVehicleId ?? '',
      vehicleBrandId:
        existingOrder.customerVehicle?.vehicleBrandId ??
        existingOrder.customerVehicle?.vehicleBrand?.id ??
        '',
      vehicleModelId:
        existingOrder.customerVehicle?.vehicleModelId ??
        existingOrder.customerVehicle?.vehicleModel?.id ??
        '',
      vehicleRegistrationNumber: existingOrder.customerVehicle?.vehicleRegistrationNumber ?? '',
      vehicleRegistrationYear: existingOrder.customerVehicle?.vehicleRegistrationYear
        ? String(existingOrder.customerVehicle?.vehicleRegistrationYear)
        : '',
      vehicleVin: existingOrder.customerVehicle?.vehicleVin ?? '',
      vehicleChassisNumber: existingOrder.customerVehicle?.vehicleChassisNumber ?? '',
      // include selected location for display
      locationId: ((existingOrder as any)?.location?.id as string) || ((existingOrder as any)?.locationId as string) || '',
    };

    return snapshot;
  }, [existingOrder]);

  const renderStepContent = () => {
    if (activeKey === 'customer-vehicle') {
      if (orderIdParam && orderLoading) {
        return (
          <div className="m-2 rounded-2xl border border-slate-200 bg-slate-50 p-2 text-sm text-slate-600">
            Loading existing order details…
          </div>
        );
      }

      if (orderIdParam && orderError) {
        return (
          <div className="m-2 rounded-2xl border border-danger/40 bg-danger/10 p-2 text-sm text-danger">
            {orderError}
          </div>
        );
      }

      return (
        <CustomerVehicleSelection
          currentUser={currentUser}
          loadingUser={loadingUser}
          orderId={orderIdParam}
          defaultSnapshot={defaultCustomerVehicleSnapshot}
        />
      );
    }

    if (activeKey === 'vehicle-inspection') {
      return (
        <InspectionChecklistPanel
          orderId={orderIdParam}
          showHeader={false}
          onCompleted={(id) => {
            const target = `/order-management/new/services-products?orderId=${id}`;
            navigate(target);
          }}
          onOrderUpdate={(updated) => setExistingOrder(updated)}
        />
      );
    } else if (activeKey === 'services-products') {
      return (
        <OrderProductServicesCart
          orderId={orderIdParam}
          showHeader={false}
          onCompleted={(id) => {
            const target = `/order-management/new/manager-approval?orderId=${id}`;
            navigate(target);
          }}
          onOpenRelatedProducts={handleOpenRelatedModal}
          onOpenRecommendedProducts={undefined}
        />
      );
    } else if (activeKey === 'manager-approval') {
      return (
        <OrderManagerApproval
          orderId={orderIdParam}
          showHeader={false}
          onCompleted={(id) => {
            const target = `/order-management/new/customer-approval?orderId=${id}`;
            navigate(target);
          }}
        />
      );
    } else if (activeKey === 'customer-approval') {
      return (
        <OrderCustomerApproval
          orderId={orderIdParam}
          showHeader={false}
          onCompleted={(id) => {
            const target = `/order-management/new/technician-work?orderId=${id}`;
            navigate(target);
          }}
        />
      );
    } else if (activeKey === 'technician-work') {
      return (
        <OrderAssignTechnician
          orderId={orderIdParam}
          showHeader={false}
          onCompleted={(id) => {
            const target = `/order-management/new/technician-completed?orderId=${id}`;
            navigate(target);
          }}
          onOrderUpdate={(updated) => setExistingOrder(updated)}
        />
      );
    } else if (activeKey === 'technician-completed') {
      return (
        <OrderTechnicianCompleted
          orderId={orderIdParam}
          showHeader={false}
          onCompleted={(id) => {
            const target = `/order-management/new/pre-delivery-checklist?orderId=${id}`;
            navigate(target);
          }}
          onOrderUpdate={(updated) => setExistingOrder(updated)}
        />
      );
    } else if (activeKey === 'pre-delivery-checklist') {
      return (
        <PreDeliveryChecklistPanel
          orderId={orderIdParam}
          showHeader={false}
          onCompleted={(id) => {
            const target = `/order-management/new/payment-pos?orderId=${id}`;
            navigate(target);
          }}
          onOrderUpdate={(updated) => setExistingOrder(updated)}
        />
      );
    } else if (activeKey === 'payment-pos') {
      return <OrderPaymentPos orderId={orderIdParam} />;
    } else if (activeKey === 'gate-pass') {
      return <OrderGatePass orderId={orderIdParam} />;
    } else {
      return (
        <OrderStepPlaceholder
          title="Place holder"
          description="Place holder description"
        >
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg border border-primary/30 px-4 py-2 text-sm font-semibold text-primary transition hover:bg-primary/10"
            onClick={() =>
              navigate(
                `/order-management/new/vehicle-inspection${orderIdParam ? `?orderId=${orderIdParam}` : ''
                }`,
              )
            }
            disabled={!orderIdParam}
          >
            Open inspection checklist
          </button>
          {!orderIdParam ? (
            <p className="mt-2 text-xs text-slate-400">
              Save the customer & vehicle selection first to generate an order before running the inspection.
            </p>
          ) : null}
        </OrderStepPlaceholder>
      );
    }
  };

  return (
    <div className="mx-auto w-full max-w-[96rem] min-h-[calc(100vh-220px)] rounded-[28px] border border-slate-200/70 bg-white/95 p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      <Notification
        className="flex w-80 max-w-full flex-col items-center gap-1 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 text-center text-primary shadow-lg"
        options={{
          duration: 2500,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true,
          close: false,
          offset: {
            x: 24,
            y: 24,
          },
        }}
        getRef={(el: NotificationElement) => {
          aiToastRef.current = el;
        }}
      >
        {aiToast ? (
          <div className="space-y-1">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">
              {aiToast.title}
            </p>
            <p className="text-xs text-primary/80">{aiToast.message}</p>
          </div>
        ) : null}
      </Notification>
      <PageHeaderWithBack
        title={isEditing ? 'Edit order' : 'Create order'}
        description={
          isEditing
            ? 'Review and update the job card. Progress through the remaining steps to complete the order.'
            : 'Follow the job card journey. Start by linking a customer and vehicle, then move into inspections, approvals, and delivery.'
        }
        backHref="/order-management"
        backLabel="Back to orders"
        actions={null}
      />

      {/* Full-width AI summary card under the header */}
      {isEditing && (
        <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/70 p-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                AI vehicle health summary
              </p>
              {aiAnalysis ? (
                (() => {
                  const scores = (aiAnalysis as any).scores ?? {};
                  const overall = scores.overall ?? {};
                  const styles = getAiHealthScoreStyles(overall.score);
                  return (
                    <p className={`mt-1 text-sm ${styles.text}`}>
                      <span className="font-semibold">
                        {overall.score ?? '—'}
                      </span>{' '}
                      <span className={`text-xs uppercase tracking-wide ${styles.label}`}>
                        {overall.label ?? ''}
                      </span>
                      {overall.summary ? (
                        <span className={`ml-1 ${styles.label}`}>
                          — {overall.summary}
                        </span>
                      ) : null}
                    </p>
                  );
                })()
              ) : (
                <p className="mt-1 text-sm text-slate-700">
                  Generate AI analysis to see vehicle health scores and recommendations.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {(existingOrder?.checklists ?? []).some((entry) => entry.type === 'inspection') ? (
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-lg border border-indigo-500 px-3 py-1.5 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleOpenAiModal}
                  disabled={aiRunning}
                >
                  {aiRunning
                    ? 'Analyzing…'
                    : aiAnalysis
                      ? 'View AI analysis'
                      : 'Generate AI analysis'}
                </button>
              ) : (
                <p className="text-xs text-slate-500">
                  Add and complete an inspection checklist to enable AI analysis.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 flex h-full flex-col gap-6 lg:flex-row">
        <aside className="flex basis-48 shrink-0 flex-col gap-1.5 rounded-2xl border border-slate-200/70 bg-[#f3f5ff] p-3 shadow-sm">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Order steps
          </h2>
          {NAV_LINKS.map((item, index) => {
            const isActive = item.key === activeKey;
            const isCompleted = completedSteps.has(item.key);
            const isDisabled = !orderIdParam && item.key !== DEFAULT_STEP_KEY;

            return (
              <button
                key={item.key}
                type="button"
                className={`flex items-center gap-2 rounded-xl px-3 py-2 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${isActive
                  ? 'bg-primary/10 text-primary shadow-sm'
                  : isDisabled
                    ? 'text-slate-400'
                    : 'text-slate-600 hover:bg-white/70 hover:text-primary'
                  }`}
                onClick={() => handleSelectNav(item.key)}
                disabled={isDisabled}
              >
                <span
                  className={`inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${isActive ? 'bg-primary text-white' : 'bg-white text-slate-600'
                    }`}
                >
                  {index + 1}
                </span>
                <span className="text-sm font-medium leading-snug">
                  {item.label}
                </span>
                {isCompleted ? (
                  <span className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <Check className="h-2 w-2" strokeWidth={3} />
                  </span>
                ) : null}
              </button>
            );
          })}
        </aside>

        <section className="min-w-0 flex-1 rounded-2xl border border-slate-200/70 bg-white shadow-sm">
          <header className="border-b border-slate-200 p-5">
            <h1 className="text-2xl font-semibold text-slate-800">
              {activeLink.label}
            </h1>
            {activeLink.description ? (
              <p className="mt-2 max-w-3xl text-sm text-slate-500">
                {activeLink.description}
              </p>
            ) : null}
          </header>

          <div className="space-y-6 p-5">
            {userError ? (
              <div className="rounded-xl border border-danger/30 bg-danger/10 p-4 text-sm text-danger">
                {userError}
              </div>
            ) : null}

            {renderStepContent()}
          </div>
        </section>
      </div>

      {aiModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 px-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                AI recommendations
              </h2>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100"
                onClick={() => setAiModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto pr-1">
              {renderAiSummary()}
            </div>
          </div>
        </div>
      ) : null}

      {relatedModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 px-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                Related products
              </h2>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100"
                onClick={() => setRelatedModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto pr-1">
              {renderRelatedSummary()}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OrderNewPage;
